/**
 * The platform Activity view: which recorded events are worth a super admin's
 * attention, and what to call them.
 *
 * The audit log records everything, including a stream of routine events (every
 * product edit, every order) that would bury the few that matter at platform
 * level. Rather than show it all and filter, the view starts from a curated list
 * of events grouped into categories. The server filters by exactly these lists
 * and the UI labels from them, so the two cannot drift.
 *
 * The action strings are the ones the API writes (`AUDIT_ACTIONS` in apps/api); a
 * test pins every entry here to a real one so a renamed action cannot silently
 * vanish from this view.
 */
/**
 * How many entries the platform Activity view keeps — events and store-access
 * visits each. Older ones are deleted: the view is for what happened recently,
 * and an unbounded log is a table that only grows.
 */
export const ACTIVITY_RETENTION_LIMIT = 10_000;
export const ACTIVITY_CATEGORIES = [
    'all',
    'access',
    'stores',
    'security',
    'money',
    'platform',
];
export const ACTIVITY_CATEGORY_LABELS = {
    all: 'All important events',
    access: 'Store access (jumps)',
    stores: 'Store suspensions',
    security: 'Sign-in & security',
    money: 'Refunds & failed payments',
    platform: 'Platform settings',
};
export const ACTIVITY_ACTIONS_BY_CATEGORY = {
    access: ['platform.support_access_started', 'platform.support_access_ended'],
    stores: ['platform.store_status_changed', 'platform.user_activated'],
    security: [
        'auth.login_failed',
        'auth.password_reset_requested',
        'auth.password_reset',
        'auth.password_changed',
        'staff.role_changed',
        'staff.removed',
    ],
    money: ['payment.refunded', 'payment.failed', 'billing.plan_paid'],
    platform: [
        'platform.cc_order_emails_changed',
        'platform.plan_updated',
        'platform.store_plan_assigned',
        'platform.billing_configured',
    ],
};
/** Every event the Activity view can show, each listed once. */
export const ALL_ACTIVITY_ACTIONS = [
    ...new Set(Object.values(ACTIVITY_ACTIONS_BY_CATEGORY).flat()),
];
/** The recorded events for a category; `all` is every category together. */
export function actionsForCategory(category) {
    return category === 'all' ? ALL_ACTIVITY_ACTIONS : ACTIVITY_ACTIONS_BY_CATEGORY[category];
}
const ACTION_LABELS = {
    'platform.support_access_started': 'Opened a store (view-only)',
    'platform.support_access_ended': 'Left a store',
    'platform.store_status_changed': 'Store status changed',
    'platform.user_activated': 'Account activated by platform',
    'auth.login_failed': 'Failed sign-in',
    'auth.password_reset_requested': 'Password reset requested',
    'auth.password_reset': 'Password reset completed',
    'auth.password_changed': 'Password changed',
    'staff.role_changed': 'Team member’s role changed',
    'staff.removed': 'Team member removed',
    'payment.refunded': 'Payment refunded',
    'payment.failed': 'Payment failed',
    'platform.cc_order_emails_changed': 'Platform order-email copy changed',
    'platform.plan_updated': 'Plan edited',
    'platform.store_plan_assigned': 'Store moved to a plan',
    'platform.billing_configured': 'Platform billing settings changed',
    'billing.plan_paid': 'Plan paid',
};
/** A readable name for a recorded action; falls back to the raw action string. */
export function labelForAction(action) {
    return ACTION_LABELS[action] ?? action;
}
/**
 * Where a support grant stands. Derived, not stored: `ENDED` means the operator
 * left early, `EXPIRED` means the time ran out, `ACTIVE` means it still works.
 */
export const SUPPORT_ACCESS_STATUSES = ['ACTIVE', 'ENDED', 'EXPIRED'];
//# sourceMappingURL=activity.js.map